"use client"
import type React from "react"
import { useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { BarChart3, Clock } from "lucide-react"

import logo from "../../assets/images/logo.png"
import { Box, Home, LogOut, Shield, Building, Building2, UsersRound, UserPlus } from "lucide-react"
import { useAuthContext } from "../../context/AuthContext"

interface DashboardLayoutProps {
  children: React.ReactNode
  title?: string
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children, title }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false)

  // const navigate = useNavigate()

  const location = useLocation()
  const { hasRole } = useAuthContext()
  // Moderator/support_user only get Watch History from this layout — Courses
  // and Package are full management surfaces they shouldn't see as menu options.
  const isLimitedRole = hasRole(["moderator", "support_user"]) && !hasRole(["admin"])
  
  const handleLogout = () => {
    // Clear local storage
    localStorage.removeItem("token")
    localStorage.removeItem("user")
    localStorage.removeItem("userData")
    localStorage.removeItem("AUTH_TOKEN")
    localStorage.removeItem("loginHistoryId")
    window.location.href = "/login"
  }
  
  const [isUserManagementOpen, setIsUserManagementOpen] = useState(false);
  
  const navItems = [
    { name: "Dashboard", icon: Home, href: "/enhanced-admin-dashboard" },
    { name: "Courses", icon: Box, href: "/courses" },
    { name: "Package", icon: Shield, href: "/package" },
    { name: "Performance Report", icon: BarChart3, href: "/admin/learnrkb" },
    { name: "Watch History", icon: Clock, href: "/admin/watch-history" },
    // { name: "User Dshboard", icon: User, href: "/admin/userlist" },
    // { name: "User Management", icon: UserCog, href: "/user-management", hasSubmenu: true },
    // { name: "Profile", icon: User, href: "/profile" },
  ].filter((item) => !isLimitedRole || !["Courses", "Package"].includes(item.name))
  
  const userManagementSubmenuItems = [
    { name: "Users", icon: UsersRound, href: "/user-management" },
    { name: "Companies", icon: Building, href: "/companies" },
    { name: "Departments", icon: Building2, href: "/departments" },
    { name: "Add User", icon: UserPlus, href: "/add-user" },
  ]
 
  return (
   <div className="flex min-h-screen min-w-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-200/10 to-purple-200/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-indigo-200/10 to-cyan-200/10 rounded-full blur-3xl"></div>
      </div>
      {/* ================= MOBILE HEADER ================= */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white shadow-sm border-b px-3 py-3">
        <div className="flex items-center justify-center">
          {/* Nav icon cards only — no brand text on mobile */}
          <nav className="flex items-center gap-2">
            {[
              { label: "Dashboard",    icon: Home,     href: "/enhanced-admin-dashboard" },
              { label: "Courses",      icon: Box,      href: "/courses"           },
              { label: "Package",      icon: Shield,   href: "/package"           },
              { label: "Performance",  icon: BarChart3, href: "/admin/learnrkb"   },
              { label: "Watch.Hist.",  icon: Clock,    href: "/admin/watch-history" },
            ].filter((item) => !isLimitedRole || !["Courses", "Package"].includes(item.label))
              .map(({ label, icon: Icon, href }) => (
              <Link
                key={label}
                to={href}
                className={`flex flex-col items-center gap-1 px-2 py-2 rounded-xl transition-colors min-w-[56px] ${
                  location.pathname === href
                    ? "bg-blue-50"
                    : "bg-gray-100 hover:bg-blue-50"
                }`}
              >
                <div className={`w-9 h-9 flex items-center justify-center rounded-xl shadow-sm ${
                  location.pathname === href ? "bg-blue-600" : "bg-white"
                }`}>
                  <Icon className={`w-5 h-5 ${location.pathname === href ? "text-white" : "text-blue-700"}`} />
                </div>
                <span className="text-[10px] font-medium text-gray-700">{label}</span>
              </Link>
            ))}
          </nav>

        </div>
      </header>

      
      {/* Mobile Sidebar Toggle */}
      {/* <div className="md:hidden fixed top-4 left-4 z-40 bg-white/90 backdrop-blur-sm rounded-2xl p-3 shadow-xl border border-white/50 transform hover:scale-105 transition-all duration-300">
        <button 
          onClick={() => setIsSidebarOpen(!isSidebarOpen)} 
          className="text-gray-700 hover:text-blue-600 focus:outline-none transition-all duration-300"
        >
          {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>

      <header className="md:hidden fixed top-0 left-0 right-0 z-40 h-14 bg-white/90 backdrop-blur-md border-b border-white/50 shadow-sm flex items-center justify-between px-4">
        
        <button
          onClick={() => setIsSidebarOpen(true)}
          className="p-2 rounded-lg hover:bg-gray-100 transition"
          aria-label="Open menu"
        >
          <Menu size={22} />
        </button>

        <Link to="/dashboard" className="flex items-center">
          <img
            src={logo}
            alt="RKB Logo"
            className="h-7 w-auto"
          />
        </Link>

        <div className="w-8" />
      </header>

      </div> */}
      
      <aside
        className={`
          flex flex-col
          z-30 bg-white text-gray-800 border-b md:border-r
          fixed top-20 left-0 right-0 md:inset-y-0 md:w-64

          transform transition-all duration-300 ease-in-out

          md:h-screen                /* desktop full height ONLY */
          md:translate-y-0 md:opacity-100 md:pointer-events-auto

          ${isSidebarOpen
            ? "translate-y-0 opacity-100"
            : "-translate-y-full opacity-0 pointer-events-none"
          }
        `}
      >
        
        <nav className="flex-1 px-0 py-0 overflow-y-auto">
          <ul>
        <div className="hidden md:flex p-6 border-b border-white/50 items-center justify-center
                  bg-gradient-to-r from-blue-50/80 to-indigo-50/80 backdrop-blur-sm">
          <Link to="/dashboard" className="flex items-center transition-all duration-300 hover:scale-105 group">
            <img 
              src={logo} 
              alt="Rakesh Bansal Ventures Logo" 
              className="h-10 w-auto transition-all duration-300 group-hover:drop-shadow-lg" 
            />
          </Link>
        </div>
            {navItems.map((item) => {
              
              if (item.name === "User Management") {
                return (
                  <li key={item.name}>
                    <div className="flex flex-col">
                      <button
                        onClick={() => {
                          setIsUserManagementOpen(!isUserManagementOpen);
                          setIsSidebarOpen(false);
                        }}
                        className={`flex items-center px-4 py-3 rounded-xl transition-all duration-300 group transform hover:scale-105 ${
                          isUserManagementOpen || location.pathname.includes('user') || location.pathname.includes('company') || location.pathname.includes('department')
                            ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold shadow-lg border border-blue-400/50"
                            : "text-gray-600 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-indigo-50/80 hover:text-blue-700 hover:shadow-md backdrop-blur-sm"
                        }`}
                      >
                        <item.icon className={`mr-3 flex-shrink-0 transition-all duration-300 ${
                          isUserManagementOpen || location.pathname.includes('user') || location.pathname.includes('company') || location.pathname.includes('department') ? "text-white" : "text-gray-500 group-hover:text-blue-600"
                        }`} size={20} />
                        <span className="font-medium text-sm flex-1 text-left">{item.name}</span>
                        <svg
                          className={`w-4 h-4 transition-transform duration-200 ${isUserManagementOpen ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {isUserManagementOpen && (
                        <ul className="mt-1 ml-6 space-y-1 bg-blue-50/30 rounded-lg p-2 backdrop-blur-sm">
                          {userManagementSubmenuItems.map((subItem) => (
                            <li key={subItem.name}>
                              <Link
                                to={subItem.href}
                                className={`flex items-center px-3 py-2 rounded-md text-xs transition-colors ${
                                  location.pathname === subItem.href
                                    ? "bg-blue-100 text-blue-700 font-medium"
                                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                                }`}                              
                                onClick={() => setIsSidebarOpen(false)}
                              >
                                <subItem.icon className="mr-2 flex-shrink-0 text-gray-500" size={16} />
                                <span className="text-xs">{subItem.name}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </li>
                );
              } else {
                return (
                  <li key={item.name}>
                <Link
                  to={item.href}
                  className={`flex items-center px-4 py-3 rounded-lg text-sm transition-colors duration-200 ${
                    location.pathname === item.href
                      ? "bg-blue-100 text-blue-700 font-semibold"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                      onClick={() => setIsSidebarOpen(false)}
                    >
                  <item.icon
                    className={`mr-3 ${
                      location.pathname === item.href
                        ? "text-blue-700"
                        : "text-blue-700"
                    }`}
                    size={18}
                  />                      
                      <span className="font-medium text-sm">{item.name}</span>
                    </Link>
                  </li>
                );
              }
            })}
          </ul>
        </nav>
        
        <div className="p-4 mt-auto border-t border-white/50 bg-gradient-to-r from-gray-50/80 to-red-50/50 backdrop-blur-sm">
          <button
            onClick={() => setShowLogoutConfirmation(true)}
            className="w-full flex items-center px-4 py-3 rounded-xl text-gray-600 hover:bg-gradient-to-r hover:from-red-500 hover:to-pink-500 hover:text-white transition-all duration-300 group transform hover:scale-105 hover:shadow-lg"
          >
            <LogOut className="mr-3 flex-shrink-0 text-gray-500 group-hover:text-white transition-all duration-300" 
            size={20} />
            <span className="font-medium text-sm">Logout</span>
          </button>
        </div>
      </aside>
      
      {/* Main Content Area - Fixed for mobile */}
      <div className="flex-1 min-w-0 min-h-screen md:ml-64 pt-28 md:pt-0 overflow-x-hidden">
        <main className="w-full min-w-0 bg-white/40 backdrop-blur-sm">
          <div className="w-full min-w-0 px-0 py-0">
            {title && (
              <div className="mb-6 pl-4 ">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mt-4">
                  {title}
                </h1>

              </div>
            )}

            <div className="relative z-10 w-full min-w-0">
              {children}
            </div>
          </div>
        </main>
      </div>
      
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 z-20 bg-black/60 md:hidden backdrop-blur-md transition-all duration-300"
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}
      
      {/* Logout Confirmation Modal */}
      {showLogoutConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Logout</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to logout? You'll need to sign in again to access your account.</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowLogoutConfirmation(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-md hover:from-red-600 hover:to-pink-600 transition-all duration-200 flex items-center shadow-sm hover:shadow-md"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DashboardLayout